import { useState, useEffect } from 'react';
import { Link, useNavigate, useBlocker } from 'react-router-dom';
import { styled } from 'styled-components';
import { useForm } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import moment from 'moment';

import { useAppContext } from '../contextApi/context';
import { searchVehiclesByPlate } from '../services/VehiclesService';
import { searchUsersByName } from '../services/UsersService';
import { getMaintenanceTypes, createWorkOrder, approveWorkOrder } from '../services/OrdersService';
import ActionInProgressModal from '../components/shared/ActionInProgressModal';
import CreateOrderConfirmationModal from '../components/shared/CreateOrderConfirmationModal';
import CustomDateTimePicker from '../components/shared/CustomDateTimePicker';
import { checkPermission } from '../utils/permissions';
import { VEHICLE_ASSIGNED_TO_ACTIVE_WORK_ORDER } from '../constants/apiMessages';

const CreateOrder = () => {
    // Context y hooks
    const { loggedUser } = useAppContext();
    const navigate = useNavigate();
    const {
        register,
        formState: { errors },
        handleSubmit,
        watch,
        reset,
    } = useForm({
        defaultValues: {
            maintenanceType: '',
            observations: '',
            odometer: '0',
        },
    });

    // Timer para búsquedas
    const [timer, setTimer] = useState(null);

    // Estado de fecha
    const [requestDate, setRequestDate] = useState(moment.utc(Date.now()).format('YYYY-MM-DDTHH:mm:ss'));
    const [showDateError, setShowDateError] = useState(false);

    // Estado de vehículo
    const [availableVehicles, setAvailableVehicles] = useState([]);
    const [selectedVehicleId, setSelectedVehicleId] = useState(null);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [showVehicleError, setShowVehicleError] = useState(false);
    const [showErrorOdometer, setShowErrorOdometer] = useState(false);

    // Estado de usuario
    const [availableUsers, setAvailableUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [selectedUserName, setSelectedUserName] = useState(null);
    const [showUserError, setShowUserError] = useState(false);
    const [isOwnOrderUser, setIsOwnOrderUser] = useState(false);

    // Estado de tipos de mantenimiento
    const [maintenanceTypes, setMaintenanceTypes] = useState([]);
    const [isLoadingMaintenanceTypes, setIsLoadingMaintenanceTypes] = useState(true);

    // Estado de confirmación y envío
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [orderData, setOrderData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Estados para manejo de errores específicos
    const [showErrorVehicleActiveOrder, setShowErrorVehicleActiveOrder] = useState(false);

    // Estado para checkbox de aprobación automática
    const [shouldApproveOnCreate, setShouldApproveOnCreate] = useState(false);
    const [canManageOrders, setCanManageOrders] = useState(false);

    // Verificación de cambios en el formulario
    const watchedFields = watch();
    const hasFormChanges = isOwnOrderUser
        ? Object.values(watchedFields).some((value) => value !== '' && value !== null) ||
          selectedVehicleId !== null ||
          requestDate !== moment.utc(Date.now()).format()
        : Object.values(watchedFields).some((value) => value !== '' && value !== null) ||
          selectedUserId !== null ||
          selectedVehicleId !== null ||
          requestDate !== moment.utc(Date.now()).format();

    // Controlador de navegación bloqueada
    let blocker = useBlocker(
        ({ currentLocation, nextLocation }) => !isSaving && hasFormChanges && currentLocation.pathname !== nextLocation.pathname
    );

    // Cargar tipos de mantenimiento al montar el componente
    useEffect(() => {
        const loadMaintenanceTypes = async () => {
            try {
                setIsLoadingMaintenanceTypes(true);
                const response = await getMaintenanceTypes(loggedUser.token);
                setMaintenanceTypes(response || []);
            } catch (error) {
                setMaintenanceTypes([]);
            } finally {
                setIsLoadingMaintenanceTypes(false);
            }
        };

        loadMaintenanceTypes();
    }, [loggedUser.token]);

    // Verificar permisos y autoseleccionar usuario SOLO si tiene add_own_work_orders pero NO add_work_orders
    useEffect(() => {
        const hasOwnOrderPermission = checkPermission(loggedUser.permissions, 'add_own_work_orders');
        const hasAddWorkOrdersPermission = checkPermission(loggedUser.permissions, 'add_work_orders');
        const hasManagePermission = checkPermission(loggedUser.permissions, 'manage_work_orders');

        // Solo bloquear y autoseleccionar si tiene ÚNICAMENTE add_own_work_orders
        const shouldLockToOwnUser = hasOwnOrderPermission && !hasAddWorkOrdersPermission;

        setIsOwnOrderUser(shouldLockToOwnUser);
        setCanManageOrders(hasManagePermission);

        // Solo autoseleccionar si el usuario está restringido a sus propias órdenes
        if (shouldLockToOwnUser && loggedUser && loggedUser.username) {
            const findAndSelectLoggedUser = async () => {
                try {
                    const response = await searchUsersByName(loggedUser.token, loggedUser.username);

                    if (response && Array.isArray(response) && response.length > 0) {
                        const currentUserFromSearch = response.find((user) => user.username === loggedUser.username);

                        if (currentUserFromSearch) {
                            // Autoseleccionar el usuario encontrado
                            setSelectedUserId(currentUserFromSearch.id);
                            setSelectedUserName(
                                `${currentUserFromSearch.firstName} ${currentUserFromSearch.lastName} (${currentUserFromSearch.username})`
                            );

                            const allUsers = response.map((user) => ({
                                id: user.id,
                                label: `${user.firstName} ${user.lastName} (${user.username})`,
                            }));
                            setAvailableUsers(allUsers);
                        }
                    }
                } catch (error) {}
            };

            findAndSelectLoggedUser();
        }
    }, [loggedUser]);

    useEffect(() => {
        if (!hasFormChanges) {
            blocker.proceed?.();
        }
    }, [hasFormChanges, blocker]);

    // Función para obtener el ID del tipo de mantenimiento
    const getMaintenanceTypeId = (typeName) => {
        const foundType = maintenanceTypes.find((type) => type.name === typeName);
        return foundType ? foundType.id : null;
    };

    // Manejo de confirmación
    const handleShowConfirmation = (data) => {
        if (isAllDataFilled(data)) {
            const payload = {
                requestDate: requestDate + 'Z',
                vehiclePlate: selectedVehicle?.plate,
                requestingUser: selectedUserName,
                maintenanceType: data.maintenanceType,
                observations: data.observations,
                vehicleId: selectedVehicleId,
                requestingUserId: selectedUserId,
            };

            // Incluir odometer en el payload si el usuario no es isOwnOrderUser y hay valor
            if (!isOwnOrderUser && data.odometer) {
                payload.odometer = data.odometer;
            }

            setOrderData(payload);
            setIsSaving(true);
            setShowConfirmation(true);
        }
    };

    const handleCloseConfirmation = () => {
        setShowConfirmation(false);
        setIsSaving(false);
    };

    // Función para crear la orden
    const handleCreateOrder = async () => {
        clearMessages();
        setIsSubmitting(true);

        try {
            // Preparar los datos para crear la orden
            const apiOrderData = {
                vehicleId: selectedVehicleId,
                maintenanceTypeId: getMaintenanceTypeId(orderData.maintenanceType),
                observations: orderData.observations,
                requestingUserId: selectedUserId,
                requestDate: orderData.requestDate,
                odometer: isOwnOrderUser ? undefined : Number(watch('odometer')),
            };

            // Solo incluir odometer si el usuario tiene permisos para gestionarlo
            if (canManageOrders && watch('odometer')) {
                apiOrderData.odometer = Number(watch('odometer'));
            }

            // Crear la orden
            const createResponse = await createWorkOrder(loggedUser.token, apiOrderData);

            // Verificar si la creación fue exitosa
            if (!createResponse.success) {
                // Manejar errores de creación
                handleCreateOrderError(createResponse);
                return;
            }

            // Orden creada exitosamente
            const createdOrderId = createResponse.data.id;
            const createdOrderNumber = createResponse.data.orderNumber;
            let approvalSuccess = false;
            let approvalAttempted = false;

            // Si el checkbox está marcado, intentar aprobar
            if (shouldApproveOnCreate && createdOrderId) {
                approvalAttempted = true;

                try {
                    const approveResponse = await approveWorkOrder(loggedUser.token, createdOrderId);

                    if (approveResponse.success) {
                        approvalSuccess = true;
                    } else {
                    }
                } catch (approveError) {}
            }

            // Limpiar el formulario
            cleanupForm();

            // Determinar el mensaje de éxito según el resultado
            let successMessage = 'Orden de trabajo creada exitosamente';
            let isPartialSuccess = false;

            if (approvalAttempted) {
                if (approvalSuccess) {
                    // Creada Y aprobada
                    successMessage = 'Orden de trabajo creada y aprobada exitosamente';
                } else {
                    // Creada pero NO aprobada
                    successMessage = 'Orden de trabajo creada exitosamente, pero no se pudo aprobar. Puede aprobarla manualmente.';
                    isPartialSuccess = true;
                }
            }

            // ]Redirigir según permisos
            const hasManagePermission = checkPermission(loggedUser.permissions, 'manage_work_orders');

            setIsSubmitting(false);
            setShowConfirmation(false);

            if (hasManagePermission) {
                // Usuario con permisos de gestión: va a la página de edición
                navigate(`/ordenes/${createdOrderNumber}`, {
                    state: {
                        orderId: createdOrderId,
                        justCreated: true,
                        showSuccessMessage: true,
                        successMessage: successMessage,
                        isPartialSuccess: isPartialSuccess,
                    },
                });
            } else {
                // Usuario sin permisos de gestión: va al listado
                navigate('/ordenes', {
                    state: {
                        justCreated: true,
                        showSuccessMessage: true,
                        successMessage: successMessage,
                    },
                });
            }
        } catch (error) {
            setIsSubmitting(false);
        }
    };

    // Función auxiliar para manejar errores de creación
    const handleCreateOrderError = (response) => {
        // Verificar diferentes estructuras de error posibles
        let errorData = null;
        if (response.data) {
            errorData = response.data;
        } else if (response.errors) {
            errorData = response.errors;
        } else if (response.error) {
            errorData = response.error;
        }

        // Verificar si es error de vehículo con orden activa
        if (errorData) {
            let hasActiveOrderError = false;

            if (typeof errorData === 'string') {
                hasActiveOrderError = errorData.includes(VEHICLE_ASSIGNED_TO_ACTIVE_WORK_ORDER);
            } else if (Array.isArray(errorData)) {
                hasActiveOrderError = errorData.some((error) => error && error.includes && error.includes(VEHICLE_ASSIGNED_TO_ACTIVE_WORK_ORDER));
            }

            if (hasActiveOrderError) {
                setShowConfirmation(false);
                setShowErrorVehicleActiveOrder(true);
                setIsSubmitting(false);
                return;
            }
        }

        setIsSubmitting(false);
    };

    // Función para limpiar el formulario después de crear exitosamente
    const cleanupForm = () => {
        reset();
        setRequestDate(moment.utc(Date.now()).format('YYYY-MM-DDTHH:mm:ss'));
        setSelectedVehicleId(null);
        setSelectedVehicle(null);
        setAvailableVehicles([]);
        setSelectedUserId(null);
        setSelectedUserName(null);
        setAvailableUsers([]);
        setShouldApproveOnCreate(false);
    };

    const handleFormErrors = () => {
        isAllDataFilled();
    };

    // Manejo de cambio de fecha
    const handleDateChange = (date) => {
        setRequestDate(date);
        setShowDateError(false);
    };

    // Manejo de cambio de kilometraje
    const handleOdometerChange = (e) => {
        setShowErrorOdometer(false);
    };

    // Manejo de selección de vehículo
    const handleSelectVehicle = (event, newValue) => {
        if (newValue != null) {
            setShowVehicleError(false);
            setSelectedVehicleId(newValue.id);
            const found = availableVehicles.find((vehicle) => vehicle.id === newValue.id);
            setSelectedVehicle(found);

            // Solo setear el kilometraje si el usuario tiene permisos para gestionarlo
            if (canManageOrders) {
                reset({
                    maintenanceType: watch('maintenanceType'),
                    observations: watch('observations'),
                    odometer: found.odometer ? found.odometer : '0',
                });
            }
        } else {
            setSelectedVehicleId(null);
            setSelectedVehicle(null);

            // Solo limpiar el kilometraje si el usuario tiene permisos para gestionarlo
            if (canManageOrders) {
                reset({
                    maintenanceType: watch('maintenanceType'),
                    observations: watch('observations'),
                    odometer: '0',
                });
            }
        }
    };

    const handleSearchVehicles = (e, newValue, reason) => {
        clearTimeout(timer);

        const newTimer = setTimeout(async () => {
            if (reason === 'clear') {
                setAvailableVehicles([]);
                setSelectedVehicleId(null);
                setSelectedVehicle(null);
            }

            if (newValue !== '') {
                if (reason === 'input') {
                    try {
                        const response = await searchVehiclesByPlate(loggedUser.token, newValue);

                        // Validar que response sea un array válido antes de usar map
                        if (response && Array.isArray(response) && response.length > 0) {
                            setAvailableVehicles(
                                response.map((vehicle) => ({
                                    id: vehicle.id,
                                    label: vehicle.plate,
                                    plate: vehicle.plate,
                                    odometer: vehicle.odometer,
                                }))
                            );
                        } else {
                            // Si no hay datos válidos, establecer array vacío
                            setAvailableVehicles([]);
                        }
                    } catch (error) {
                        // En caso de error, establecer array vacío
                        setAvailableVehicles([]);
                    }
                }
            } else {
                setAvailableVehicles([]);
                setSelectedVehicleId(null);
                setSelectedVehicle(null);
            }
        }, 500);

        setTimer(newTimer);
    };

    // Manejo de selección de usuario
    const handleSelectUser = (event, newValue) => {
        if (newValue != null) {
            setShowUserError(false);
            setSelectedUserId(newValue.id);
            setSelectedUserName(newValue.label);
        } else {
            setSelectedUserId(null);
            setSelectedUserName(null);
        }
    };

    const handleSearchUsers = (e, newValue, reason) => {
        clearTimeout(timer);

        const newTimer = setTimeout(async () => {
            if (reason === 'clear') {
                setAvailableUsers([]);
                setSelectedUserId(null);
                setSelectedUserName(null);
            }

            if (newValue !== '') {
                if (reason === 'input') {
                    try {
                        const response = await searchUsersByName(loggedUser.token, newValue);

                        // Validar que response sea un array válido antes de usar map
                        if (response && Array.isArray(response) && response.length > 0) {
                            setAvailableUsers(
                                response.map((user) => ({
                                    id: user.id,
                                    label: `${user.firstName} ${user.lastName} (${user.username})`,
                                }))
                            );
                        } else {
                            // Si no hay datos válidos, establecer array vacío
                            setAvailableUsers([]);
                        }
                    } catch (error) {
                        // En caso de error, establecer array vacío
                        setAvailableUsers([]);
                    }
                }
            } else {
                setAvailableUsers([]);
                setSelectedUserId(null);
                setSelectedUserName(null);
            }
        }, 500);

        setTimer(newTimer);
    };

    // Validación de datos completos
    const isAllDataFilled = (data = null) => {
        clearMessages();
        let allGood = true;

        if (!requestDate) {
            allGood = false;
            setShowDateError(true);
        }

        // Validar que la fecha y hora no sean futuras
        if (requestDate > moment.utc(Date.now()).format('YYYY-MM-DDTHH:mm:ss')) {
            allGood = false;
            setShowDateError(true);
        }

        if (selectedVehicleId === null) {
            allGood = false;
            setShowVehicleError(true);
        }

        if (selectedUserId === null) {
            allGood = false;
            setShowUserError(true);
        }

        // Validar kilometraje solo si NO es isOwnOrderUser (solo si se muestra el campo)
        if (!isOwnOrderUser && selectedVehicle && data && data.odometer) {
            if (Number(data.odometer) < selectedVehicle?.odometer) {
                allGood = false;
                setShowErrorOdometer(true);
            }
        }

        return allGood;
    };

    // Función clearMessages
    const clearMessages = () => {
        setShowErrorVehicleActiveOrder(false);
        setShowDateError(false);
        setShowVehicleError(false);
        setShowUserError(false);
        setShowErrorOdometer(false);
    };

    return (
        <div className='container mt-4'>
            {/* Mensajes de estado   */}

            {showErrorVehicleActiveOrder && (
                <div className='alert alert-danger text-center mb-1' role='alert'>
                    El vehículo seleccionado ya tiene una orden de trabajo activa.
                </div>
            )}

            <div className='mb-3'>
                <Link to='/ordenes'>Volver a gestión de órdenes</Link>
            </div>

            <h1 className='mb-5'>Crear Orden de Trabajo</h1>

            <ContainerStyled>
                <h3 className='mt-4'>Datos generales</h3>
                <form onSubmit={handleSubmit(handleShowConfirmation, handleFormErrors)}>
                    <label className='d-flex mb-1'>Fecha de solicitud</label>
                    <CustomDateTimePicker
                        defaultDate={moment(Date.now())}
                        minDate={moment(Date.now()).subtract(6, 'months')}
                        maxDate={moment(Date.now())}
                        actionHandler={handleDateChange}
                    />
                    <span className='my-1' role='alert'>
                        {showDateError && !requestDate && `Campo requerido`}
                        {showDateError &&
                            requestDate &&
                            requestDate > moment.utc(Date.now()).format('YYYY-MM-DDTHH:mm:ss') &&
                            `Fecha y hora deben ser menor o igual a la actual`}
                    </span>

                    <label className='d-flex mb-1'>Vehículo</label>
                    <Autocomplete
                        disablePortal
                        id='vehicle-select'
                        size='small'
                        options={availableVehicles}
                        renderInput={(params) => <TextField {...params} label='Buscar por placa' />}
                        onChange={handleSelectVehicle}
                        onInputChange={handleSearchVehicles}
                        noOptionsText={'Buscar por placa'}
                    />
                    <span className={`my-1 ${selectedVehicle && 'info'}`} role='alert'>
                        {showVehicleError && `Campo requerido`}
                        {selectedVehicle && `Kilometraje actual: ${selectedVehicle.odometer ? selectedVehicle.odometer : '0'} km`}
                    </span>

                    {/* Campo de kilometraje - Solo se muestra si el usuario NO tiene permisos de órdenes propias únicamente */}
                    {!isOwnOrderUser && (
                        <>
                            <label className='d-flex mb-1' htmlFor='odometer'>
                                Kilometraje
                            </label>
                            <input
                                className='form-control'
                                type='text'
                                id='odometer'
                                autoComplete='off'
                                maxLength={10}
                                {...register('odometer', {
                                    required: !isOwnOrderUser,
                                    onChange: handleOdometerChange,
                                    maxLength: 10,
                                    pattern: /^\d*\.?\d*$/,
                                })}
                            />
                            <span className='my-1' role='alert'>
                                {errors.odometer?.type === 'required' && `Campo requerido`}
                                {errors.odometer?.type === 'maxLength' && `Formato inválido`}
                                {errors.odometer?.type === 'pattern' && `Sólo se permiten números`}
                                {showErrorOdometer && `Debe ser igual o mayor al kilometraje actual`}
                            </span>
                        </>
                    )}

                    <label className='d-flex mb-1'>Solicitante</label>
                    <Autocomplete
                        disablePortal
                        id='user-select'
                        size='small'
                        options={availableUsers}
                        value={availableUsers.find((user) => user.id === selectedUserId) || null}
                        disabled={isOwnOrderUser}
                        renderInput={(params) => <TextField {...params} label='Buscar por nombre, apellido o usuario' />}
                        onChange={handleSelectUser}
                        onInputChange={handleSearchUsers}
                        noOptionsText='Buscar por nombre, apellido o usuario'
                    />
                    <span className='my-1' role='alert'>
                        {showUserError && `Campo requerido`}
                    </span>

                    <label className='d-flex mb-1' htmlFor='maintenanceType'>
                        Tipo de mantenimiento
                    </label>
                    <select
                        className='form-select'
                        id='maintenanceType'
                        {...register('maintenanceType', { required: true })}
                        disabled={isLoadingMaintenanceTypes}
                    >
                        <option value=''>{isLoadingMaintenanceTypes ? 'Cargando...' : 'Seleccione una opción'}</option>
                        {maintenanceTypes.map((type) => (
                            <option key={type.id} value={type.name}>
                                {type.name}
                            </option>
                        ))}
                    </select>
                    <span className='my-1' role='alert'>
                        {errors.maintenanceType?.type === 'required' && `Campo requerido`}
                    </span>

                    <label className='d-flex mb-1' htmlFor='observations'>
                        Observaciones
                    </label>
                    <textarea
                        className='form-control'
                        id='observations'
                        maxLength={300}
                        rows='4'
                        placeholder='Descripción detallada del problema o trabajo requerido...'
                        {...register('observations', {
                            required: 'Campo requerido',
                            minLength: {
                                value: 5,
                                message: 'Mínimo 5 caracteres',
                            },
                            maxLength: {
                                value: 300,
                                message: 'Máximo 300 caracteres',
                            },
                        })}
                    />
                    <span className='my-1' role='alert'>
                        {errors.observations?.type === 'required' && errors.observations.message}
                        {errors.observations?.type === 'minLength' && errors.observations.message}
                        {errors.observations?.type === 'maxLength' && errors.observations.message}
                    </span>

                    {/* Checkbox para aprobar automáticamente - Solo visible si tiene permiso */}
                    {canManageOrders && (
                        <ApprovalCheckboxContainer>
                            <label className='checkbox-wrapper'>
                                <input
                                    type='checkbox'
                                    id='approveOnCreate'
                                    checked={shouldApproveOnCreate}
                                    onChange={(e) => setShouldApproveOnCreate(e.target.checked)}
                                />
                                <span className='checkmark'></span>
                                <span className='checkbox-label'>Aprobar orden inmediatamente después de crearla</span>
                            </label>
                            <span className='checkbox-hint'>
                                La orden pasará automáticamente al estado "Aprobada" y estará lista para iniciar mantenimiento
                            </span>
                        </ApprovalCheckboxContainer>
                    )}

                    <div className='d-flex justify-content-center mt-4'>
                        <Link to='/ordenes'>
                            <button className='btn btn-secondary me-3' type='button'>
                                Cancelar
                            </button>
                        </Link>
                        <button className='btn btn-danger' type='submit'>
                            Guardar
                        </button>
                    </div>
                </form>
            </ContainerStyled>

            {/* Modal de confirmación */}
            {showConfirmation && orderData && (
                <CreateOrderConfirmationModal
                    data={orderData}
                    closeHandler={handleCloseConfirmation}
                    actionHandler={handleCreateOrder}
                    show={showConfirmation}
                    isSubmitting={isSubmitting}
                />
            )}

            {/* Modal de navegación bloqueada */}
            {blocker && blocker.state === 'blocked' && (
                <ActionInProgressModal
                    show={true}
                    actionHandler={() => blocker.proceed?.()}
                    closeHandler={() => blocker.reset?.()}
                    message='Tiene una orden de trabajo en progreso. ¿Desea salir?'
                />
            )}
        </div>
    );
};

// Estilos del componente
const ContainerStyled = styled.div`
    margin-bottom: 50px;

    form {
        border: #dee2e6 solid 1px;
        display: grid;
        margin: 30px auto;
        padding: 30px;
        width: 50%;

        > label {
            font-size: 15px;
            font-weight: bold;
        }

        > span {
            color: #d04437;
            font-size: 13px;
            min-height: 12px;

            &.info {
                color: green;
                font-weight: 500;
            }
        }

        select.form-control {
            padding: 8px;
            border: 1px solid #ced4da;
            border-radius: 4px;

            &:focus {
                border-color: #80bdff;
                box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
                outline: 0;
            }
        }

        .MuiAutocomplete-root {
            width: 100%;
        }

        .spinner-border-sm {
            width: 1rem;
            height: 1rem;
        }
    }

    button.btn-danger {
        background-color: #d04437;
        border-color: #d04437;

        &:hover {
            background-color: #b73d32;
            border-color: #b73d32;
        }

        &:disabled {
            background-color: #6c757d;
            border-color: #6c757d;
        }
    }

    button.btn-secondary {
        background-color: #6c757d;
        border-color: #6c757d;

        &:hover {
            background-color: #5a6268;
            border-color: #545b62;
        }
    }

    @media (max-width: 768px) {
        form {
            width: 90%;
            padding: 20px;
        }
    }
`;

const ApprovalCheckboxContainer = styled.div`
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 15px;
    margin-top: 0;
    margin-bottom: 0;

    .checkbox-wrapper {
        display: flex;
        align-items: flex-start;
        cursor: pointer;
        position: relative;
        user-select: none;
        gap: 12px;

        input[type='checkbox'] {
            position: absolute;
            opacity: 0;
            cursor: pointer;
            height: 0;
            width: 0;

            &:checked ~ .checkmark {
                background-color: #d04437;
                border-color: #d04437;

                &:after {
                    display: block;
                }
            }

            &:focus ~ .checkmark {
                box-shadow: 0 0 0 0.2rem rgba(208, 68, 55, 0.25);
            }
        }

        .checkmark {
            position: relative;
            height: 20px;
            width: 20px;
            min-width: 20px;
            background-color: #fff;
            border: 2px solid #ced4da;
            border-radius: 4px;
            transition: all 0.2s ease;
            margin-top: 2px;

            &:after {
                content: '';
                position: absolute;
                display: none;
                left: 5px;
                top: 1px;
                width: 6px;
                height: 11px;
                border: solid white;
                border-width: 0 2px 2px 0;
                transform: rotate(45deg);
            }
        }

        .checkbox-label {
            font-size: 15px;
            font-weight: 500;
            color: #495057;
            line-height: 1.4;
        }

        &:hover .checkmark {
            border-color: #d04437;
        }
    }

    .checkbox-hint {
        display: block;
        font-size: 13px;
        color: #6c757d;
        margin-top: 8px;
        margin-left: 32px;
        line-height: 1.4;
    }
`;

export default CreateOrder;
